import React, { useContext, useEffect, useState } from 'react';

import { X } from 'lucide-react';
import { Link } from 'react-router';

import AJAX from 'common/AJAX';
import { AutopilotImportErrorMessages } from 'common/constants/autopilotImport';
import { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import { type LocalFile, MimeTypes } from 'common/constants/files';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import ErrorMessage from 'common/ErrorMessage';
import FileRenderer from 'common/file/FileRenderer';
import { parseToLocalFile } from 'common/file/utils/parseToLocalFile';
import Form from 'common/Form';
import FormField from 'common/FormField';
import useMutation from 'common/hooks/useMutation';
import FileInput from 'common/inputs/FileInput';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UserCompanySelect from 'common/subdomain/admin/UserCompanySelect';
import ButtonV2 from 'common/ui/ButtonV2';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import IconButtonV2 from 'common/ui/IconButtonV2';
import Popover, { type PopoverContentProps } from 'common/ui/Popover';
import TextareaV2 from 'common/ui/TextareaV2';
import TextInputV2 from 'common/ui/TextInputV2';
import VStack from 'common/ui/VStack';

import CircleCheck from 'img/icons/circle-check.svg';

import type { AutopilotImportData } from 'common/api/endpoints/enqueue';
import type { SuccessResponse } from 'common/api/responses';

const AcceptedMimeTypes = [...MimeTypes.csv];

type AdminAutopilotImportPopoverProps = Pick<
  PopoverContentProps,
  'side' | 'align' | 'sideOffset'
> & {
  trigger: React.ReactNode;
};

type LocalFileWithOriginal = LocalFile & {
  originalFile: File;
};

const AdminAutopilotImportPopover = ({
  trigger,
  side,
  align,
  sideOffset,
}: AdminAutopilotImportPopoverProps) => {
  const showToast = useContext(ShowToastContext);
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [userID, setUserID] = useState<string>();
  const [thirdPartyCompanyID, setThirdPartyCompanyID] = useState<string>();
  const [text, setText] = useState<string>();
  const [sourceURL, setSourceURL] = useState<string>();

  const [files, setFiles] = useState<LocalFileWithOriginal[]>([]);

  useEffect(() => {
    if (location.query.autopilotImport === 'true') {
      setIsOpen(true);
    }
  }, [location.query.autopilotImport]);

  const sendAutopilot = useMutation<SuccessResponse, AutopilotImportData>({
    mutationFn: async (input) => {
      if (input.files && input.files.length > 0) {
        return AJAX.postFiles('/api/queue/import', input.files);
      } else {
        return AJAX.post('/api/queue/import', input);
      }
    },
    errors: AutopilotImportErrorMessages,
    onSuccess: () => {
      setIsSuccess(true);
      showToast('Feedback sent to autopilot', ToastTypes.success);
      resetForm();
    },
    onError: () => showToast('Failed to send to autopilot', ToastTypes.error),
  });

  const resetForm = () => {
    setThirdPartyCompanyID(undefined);
    setUserID(undefined);
    setText(undefined);
    setSourceURL(undefined);
    setFiles([]);
  };

  const handleSubmit = () => {
    if ((!userID || !text) && !hasFiles) {
      return;
    }

    sendAutopilot.mutate({
      userID: userID ?? null,
      thirdPartyCompanyID: thirdPartyCompanyID ?? null,
      text: text ?? undefined,
      files: hasFiles ? files.map((f) => f.originalFile) : undefined,
      sourceURL: sourceURL ?? undefined,
    });
  };

  const handleFileUpload = (file: File) => {
    const localFile = parseToLocalFile(file);

    if (files.some((f) => f.name === localFile.name)) {
      return;
    }

    setFiles((prevFiles) => [
      ...prevFiles,
      {
        ...localFile,
        originalFile: file,
      },
    ]);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      if (location.query.autopilotImport === 'true') {
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, autopilotImport: undefined },
        });
      }
    }
  };

  const handleFileRemoved = (file: LocalFile) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
  };

  const hasFiles = files.length > 0;
  const canSubmit = (userID && text) || hasFiles;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Content side={side} align={align} sideOffset={sideOffset} className="p-0 w-[450px]">
        <HStack className="base-border-b items-center justify-between px-4 py-2.5">
          <h3 className="text-lg font-semibold">Send to Autopilot</h3>
          <Popover.CloseButton asChild>
            <IconButtonV2 aria-label="Close" icon={X} size="small" variant="plain" />
          </Popover.CloseButton>
        </HStack>
        {isSuccess && (
          <AutopilotImportSuccess
            onSendMoreFeedback={() => setIsSuccess(false)}
            onSeeInbox={() => setIsOpen(false)}
          />
        )}
        {!isSuccess && (
          <>
            <VStack className="gap-3 p-4">
              <p className="text-base text-gray-110">
                Submit content you want Autopilot to review for feedback. Ideas and insights found
                will be created accordingly on behalf of your customers.
              </p>
              <Form
                acceptedFileTypes={AcceptedMimeTypes}
                allowFileUpload={true}
                dropZoneZIndex={101}
                onFile={handleFileUpload}>
                <VStack className="gap-3">
                  <FormField
                    uppercase={false}
                    label={
                      <HStack className="items-center gap-1">
                        {hasFiles ? null : 'Details or'}
                        <FileInput
                          accept={AcceptedMimeTypes.join(',')}
                          defaultStyle={false}
                          onFile={handleFileUpload}
                          value={
                            <ButtonV2 variant="plain" className="!p-0 underline">
                              upload a .csv
                            </ButtonV2>
                          }
                        />
                      </HStack>
                    }>
                    {hasFiles ? (
                      <FileRenderer files={files} onFileRemoved={handleFileRemoved} allowRemove />
                    ) : (
                      <TextareaV2
                        rows={4}
                        placeholder="Any text, meeting notes, emails, etc."
                        onChange={(e) => setText(e.target.value)}
                        autoFocus
                      />
                    )}
                  </FormField>
                  {!hasFiles && (
                    <>
                      <FormField uppercase={false} label="Who gave the feedback?">
                        <HStack className="gap-2">
                          <UserCompanySelect
                            onChange={({ userID, thirdPartyCompanyID }) => {
                              setUserID(userID);
                              setThirdPartyCompanyID(thirdPartyCompanyID);
                            }}
                          />
                        </HStack>
                      </FormField>
                      <FormField uppercase={false} label="Source">
                        <TextInputV2
                          size="medium"
                          placeholder="Source URL"
                          onChange={setSourceURL}
                        />
                      </FormField>
                    </>
                  )}
                </VStack>
              </Form>
              {sendAutopilot.error && <ErrorMessage>{sendAutopilot.error.message}</ErrorMessage>}
            </VStack>
            <HStack className="px-4 pb-4 justify-end gap-2">
              <Popover.CloseButton asChild>
                <ButtonV2 variant="outlined">Cancel</ButtonV2>
              </Popover.CloseButton>
              <ButtonV2
                onClick={handleSubmit}
                loading={sendAutopilot.isLoading}
                disabled={!canSubmit}>
                Submit
              </ButtonV2>
            </HStack>
          </>
        )}
      </Popover.Content>
    </Popover>
  );
};

type AutopilotImportSuccessProps = {
  onSendMoreFeedback: () => void;
  onSeeInbox: () => void;
};

const AutopilotImportSuccess = ({
  onSendMoreFeedback,
  onSeeInbox,
}: AutopilotImportSuccessProps) => {
  return (
    <VStack className="gap-6 py-32">
      <Flex className="justify-center items-center">
        <LazyLoadedImage src={CircleCheck} alt="Success" className="size-12" />
      </Flex>
      <VStack className="text-center gap-1">
        <h3 className="text-base font-semibold">Feedback sent</h3>
        <p className="text-base text-balance text-gray-110">
          Any feedback found will show in your Autopilot tab within the next 10 minutes.
        </p>
      </VStack>
      <HStack className="gap-2.5 justify-center">
        <ButtonV2 variant="outlined" asChild>
          <Link to={`/admin/autopilot?source=${SyncIntegrationNames.imports}`} onClick={onSeeInbox}>
            See Autopilot
          </Link>
        </ButtonV2>
        <ButtonV2 onClick={onSendMoreFeedback}>Send more feedback</ButtonV2>
      </HStack>
    </VStack>
  );
};

export default AdminAutopilotImportPopover;
